import React, { useState } from 'react';
import {
  Stack,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Box,
} from '@mui/material';
import TextFieldWithCheck from '@/components/form/TextFieldWithCheck.tsx';
import { investmentTypeLabels } from '@/features/investments/constants.ts';
import {
  InvestmentType,
  PsiPricingModel,
  PayoutInfo,
  PsiService,
  PsiServiceConfig,
  PsiFeeType,
} from '@inversor-hub/types';
import { pricingModelLabels, psiServiceLabels } from './constants.ts';
import { validateIBAN } from '@/utils/misc.ts';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import PercentageField from '@/components/form/PercentageField.tsx';
import ProvincesSelector from '@/components/form/ProvincesSelector.tsx';

export interface PsiConfigForm {
  volume: number | null;
  totalClosedDeals: string;
  specialistInvestmentTypes: InvestmentType[];
  historicalAvgReturn: number | null;
  averageTicket: number | null;
  coveredProvinces: string[];
  companyName: string;
  pricingModel: PsiPricingModel;
  feeAmount: number | null;
  feeReturnAmount: number | null;
  serviceConfigs: PsiServiceConfig[];
  payoutInfo: PayoutInfo;
}

interface PsiConfigFieldsProps {
  value: PsiConfigForm;
  onChange: (config: PsiConfigForm) => void;
  disabled?: boolean;
  includePayoutInfo?: boolean;
}

const getPercentageHelperText = (service: PsiService): string => {
  const labels: Record<PsiService, string> = {
    OPPORTUNITY_SEARCH: 'Porcentaje del precio de compra',
    REFURBISHMENT: 'Porcentaje del coste de reforma',
    TENANT_SEARCH: 'Porcentaje del alquiler anual',
    RENTAL_MANAGEMENT: 'Porcentaje del alquiler anual',
  };
  return labels[service] || '';
};

const PsiConfigFields: React.FC<PsiConfigFieldsProps> = ({
  value: psiConfig,
  onChange: setPsiConfig,
  disabled,
  includePayoutInfo = true,
}) => {
  const [ibanError, setIbanError] = useState<string | null>(null);

  const handleChange = (field: keyof PsiConfigForm, value: unknown) => {
    setPsiConfig({ ...psiConfig, [field]: value });
  };

  const handleSpecialistInvestmentTypeChange = (value: InvestmentType) => {
    const current = psiConfig.specialistInvestmentTypes;
    handleChange(
      'specialistInvestmentTypes',
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    );
  };

  const handleServiceToggle = (service: PsiService) => {
    const currentServices = psiConfig.serviceConfigs;
    const serviceExists = currentServices.find((s) => s.service === service);

    if (serviceExists) {
      handleChange(
        'serviceConfigs',
        currentServices.filter((s) => s.service !== service)
      );
    } else {
      handleChange('serviceConfigs', [
        ...currentServices,
        { service, feeType: 'FIXED', fee: undefined, percentage: undefined },
      ]);
    }
  };

  const handleServiceFeeChange = (service: PsiService, fee: number | null) => {
    const updatedConfigs = psiConfig.serviceConfigs.map((config) =>
      config.service === service ? { ...config, fee: fee ?? undefined } : config
    );
    handleChange('serviceConfigs', updatedConfigs);
  };

  const handleServiceFeeTypeChange = (service: PsiService, feeType: PsiFeeType) => {
    const updatedConfigs = psiConfig.serviceConfigs.map((config) =>
      config.service === service
        ? { ...config, feeType, fee: undefined, percentage: undefined }
        : config
    );
    handleChange('serviceConfigs', updatedConfigs);
  };

  const handleServicePercentageChange = (service: PsiService, percentage: number | null) => {
    const updatedConfigs = psiConfig.serviceConfigs.map((config) =>
      config.service === service ? { ...config, percentage: percentage ?? undefined } : config
    );
    handleChange('serviceConfigs', updatedConfigs);
  };

  const isServiceSelected = (service: PsiService) => {
    return psiConfig.serviceConfigs.some((config) => config.service === service);
  };

  const handleIbanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPsiConfig({
      ...psiConfig,
      payoutInfo: { ...psiConfig.payoutInfo, iban: value },
    });
    if (value && !validateIBAN(value)) {
      setIbanError('El IBAN no es válido');
    } else {
      setIbanError(null);
    }
  };

  const handleIbanBlur = () => {
    if (psiConfig.payoutInfo.iban && !validateIBAN(psiConfig.payoutInfo.iban)) {
      setIbanError('El IBAN no es válido');
    } else {
      setIbanError(null);
    }
  };

  return (
    <Stack spacing={2}>
      <EurCurrencyField
        id={'volume'}
        label="Volumen gestionado (€)"
        value={psiConfig.volume}
        onChange={(v) => handleChange('volume', v)}
        disabled={disabled}
        allowDecimalScale={false}
      />
      <TextField
        label="Nº total de operaciones cerradas"
        value={psiConfig.totalClosedDeals}
        onChange={(e) => handleChange('totalClosedDeals', e.target.value)}
        type="number"
        fullWidth
        disabled={disabled}
      />
      <Typography variant="subtitle1" fontWeight="bold">
        Tipos de operación favoritas
      </Typography>
      <FormGroup>
        {Object.entries(investmentTypeLabels).map(([value, label]) => (
          <FormControlLabel
            key={value}
            control={
              <Checkbox
                checked={psiConfig.specialistInvestmentTypes.includes(value as InvestmentType)}
                onChange={() => handleSpecialistInvestmentTypeChange(value as InvestmentType)}
                disabled={disabled}
              />
            }
            label={label}
          />
        ))}
      </FormGroup>
      <PercentageField
        id={'historicalAvgReturn'}
        label="Rentabilidad media histórica (%)"
        value={psiConfig.historicalAvgReturn}
        onChange={(v) => handleChange('historicalAvgReturn', v)}
        disabled={disabled}
        allowDecimalScale={true}
      />
      <EurCurrencyField
        id={'averageTicket'}
        label="Ticket medio manejado (€)"
        value={psiConfig.averageTicket}
        onChange={(v) => handleChange('averageTicket', v)}
        disabled={disabled}
        allowDecimalScale={false}
      />
      <ProvincesSelector
        value={psiConfig.coveredProvinces}
        onChange={(newValue) => handleChange('coveredProvinces', newValue)}
        disabled={disabled}
      />
      <TextFieldWithCheck
        id="companyName"
        name="companyName"
        label="Nombre comercial (opcional)"
        helperText="La razón social para los contratos se configura en Datos de contratación."
        variant="outlined"
        value={psiConfig.companyName}
        onChange={({ target }) => handleChange('companyName', target.value)}
        disabled={disabled}
      />
      <Typography variant="subtitle1" fontWeight="bold">
        Modelo de cobro
      </Typography>
      <RadioGroup
        value={psiConfig.pricingModel}
        onChange={(e) => handleChange('pricingModel', e.target.value as PsiPricingModel)}
      >
        {Object.entries(pricingModelLabels).map(([value, label]) => (
          <FormControlLabel
            key={value}
            value={value}
            control={<Radio disabled={disabled} />}
            label={label}
          />
        ))}
      </RadioGroup>

      {psiConfig.pricingModel === 'PACKAGE' && (
        <EurCurrencyField
          id="feeAmount"
          name="feeAmount"
          label="Precio del paquete completo (€)"
          value={psiConfig.feeAmount}
          onChange={(v) => handleChange('feeAmount', v)}
          disabled={disabled}
        />
      )}

      <Typography variant="subtitle1" fontWeight="bold">
        Servicios ofrecidos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Selecciona los servicios que ofreces como PSI
      </Typography>

      {psiConfig.pricingModel === 'PACKAGE' ? (
        // Package pricing: only show service selection
        <FormGroup>
          {Object.entries(psiServiceLabels).map(([value, label]) => (
            <FormControlLabel
              key={value}
              control={
                <Checkbox
                  checked={isServiceSelected(value as PsiService)}
                  onChange={() => handleServiceToggle(value as PsiService)}
                  disabled={disabled}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>
      ) : (
        // Per-service pricing: show service selection with individual pricing
        <Stack spacing={2}>
          {Object.entries(psiServiceLabels).map(([value, label]) => {
            const serviceConfig = psiConfig.serviceConfigs.find((s) => s.service === value);
            const isSelected = isServiceSelected(value as PsiService);

            return (
              <Box
                key={value}
                sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleServiceToggle(value as PsiService)}
                      disabled={disabled}
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight="medium">
                      {label}
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
                {isSelected && (
                  <Stack spacing={2} sx={{ mt: 2, ml: 4 }}>
                    <RadioGroup
                      value={serviceConfig?.feeType || 'FIXED'}
                      onChange={(e) =>
                        handleServiceFeeTypeChange(
                          value as PsiService,
                          e.target.value as PsiFeeType
                        )
                      }
                      row
                    >
                      <FormControlLabel
                        value="FIXED"
                        control={<Radio disabled={disabled} />}
                        label="Cantidad fija"
                      />
                      <FormControlLabel
                        value="PERCENTAGE"
                        control={<Radio disabled={disabled} />}
                        label="Porcentaje"
                      />
                    </RadioGroup>

                    {serviceConfig?.feeType === 'FIXED' ? (
                      <EurCurrencyField
                        id={`serviceFee-${value}`}
                        label="Precio del servicio (€)"
                        value={serviceConfig.fee ?? null}
                        onChange={(v) => handleServiceFeeChange(value as PsiService, v)}
                        disabled={disabled}
                      />
                    ) : (
                      <PercentageField
                        id={`servicePercentage-${value}`}
                        label="Porcentaje del servicio (%)"
                        value={serviceConfig?.percentage ?? null}
                        onChange={(v) => handleServicePercentageChange(value as PsiService, v)}
                        disabled={disabled}
                        helperText={getPercentageHelperText(value as PsiService)}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      <EurCurrencyField
        id="feeReturnAmount"
        name="feeReturnAmount"
        label="Penalización desestimiento"
        value={psiConfig.feeReturnAmount}
        onChange={(v) => handleChange('feeReturnAmount', v)}
        disabled={disabled}
      />

      {includePayoutInfo && (
        <>
          <Typography variant="subtitle1" fontWeight="bold">
            Información de pago
          </Typography>
          <TextFieldWithCheck
            id="iban"
            name="iban"
            label="Número de Cuenta (IBAN)"
            variant="outlined"
            value={psiConfig.payoutInfo.iban}
            onChange={handleIbanChange}
            onBlur={handleIbanBlur}
            error={!!ibanError}
            helperText={ibanError || ''}
            disabled={disabled}
          />
          <TextFieldWithCheck
            id="bankName"
            name="bankName"
            label="Nombre Entidad"
            variant="outlined"
            value={psiConfig.payoutInfo.bankName}
            onChange={({ target }) =>
              setPsiConfig({
                ...psiConfig,
                payoutInfo: { ...psiConfig.payoutInfo, bankName: target.value },
              })
            }
            disabled={disabled}
          />
          <TextFieldWithCheck
            id="accountHolderName"
            name="accountHolderName"
            label="Titular de la Cuenta"
            variant="outlined"
            value={psiConfig.payoutInfo.accountHolderName}
            onChange={({ target }) =>
              setPsiConfig({
                ...psiConfig,
                payoutInfo: { ...psiConfig.payoutInfo, accountHolderName: target.value },
              })
            }
            disabled={disabled}
          />
        </>
      )}
    </Stack>
  );
};

export default PsiConfigFields;
